import React from "react";
import PropTypes from "prop-types";
import styled, { ThemeProvider } from "styled-components";
import { space, layout, typography, color, compose } from "styled-system";
import { css } from "@styled-system/css";
import shouldForwardProp from "@styled-system/should-forward-prop";
import { themeGet } from "@styled-system/theme-get";

const WrapperStyles = compose(space, layout);

const LabelStyles = compose(space, layout, typography, color);

const InputStyles = compose(space, layout, typography, color);

/** Default cap (~8–10 lines) before the field scrolls instead of growing. */
export const DEFAULT_AUTO_GROW_MAX_HEIGHT = 200;

const AUTO_GROW_PROPS = ["autoGrow", "autoGrowMaxHeight"];

/**
 * `field-sizing: content` grows the field natively — no measuring, so it also
 * works when mounted inside a hidden (`display: none`) container. `rows` stops
 * applying once it is on, so floor the box at `rows` lines (border-box).
 * Browsers without support ignore it and keep the fixed `rows` height.
 */
const autoGrowStyles = (props) => ({
  fieldSizing: "content",
  resize: "none",
  minHeight: `calc(${Number(props.rows) || 1} * 1lh + 2 * ${themeGet("space.s")(
    props
  )} + 2 * ${themeGet("borderWidths.1")(props)})`,
  maxHeight: `${props.autoGrowMaxHeight}px`,
});

const Wrapper = styled("div").withConfig({ shouldForwardProp })(
  (props) =>
    css({
      position: "relative",
      width: props.fullWidth ? "100%" : "auto",
    }),
  WrapperStyles
);

const Input = styled("textarea")
  .withConfig({
    shouldForwardProp: (prop) =>
      !AUTO_GROW_PROPS.includes(prop) && shouldForwardProp(prop),
  })
  .attrs((props) => ({
    "data-testid": props["data-testid"] ? props["data-testid"] : null,
  }))(
  (props) =>
    css({
      display: "block",
      cursor: "text",
      appearance: "none",
      boxShadow: "none",
      fontFamily: themeGet("fonts.main")(props),
      fontSize: themeGet("fontSizes.2")(props),
      zIndex: "1",
      borderRadius: themeGet("radii.2")(props),
      // autoGrow must not transition height — `all` lags per-keystroke growth.
      transition: props.autoGrow
        ? "border-color 200ms ease-in-out, box-shadow 200ms ease-in-out"
        : themeGet("transition.transitionDefault")(props),
      background: props.disabled
        ? themeGet("colors.disabledSurface")(props)
        : themeGet("colors.surface")(props),
      color: props.disabled
        ? themeGet("colors.disabledText")(props)
        : themeGet("colors.bodyText")(props),
      width: props.fullWidth ? "100%" : "auto",
      padding: "s",
      borderWidth: themeGet("borderWidths.1")(props),
      borderStyle: "solid",
      borderColor: props.invalid
        ? themeGet("colors.dangerLight")(props)
        : props.valid
          ? themeGet("colors.successLight")(props)
          : themeGet("colors.surfaceRaisedBorder")(props),
      resize: props.resize || "both",
      "&:hover": {
        borderColor: props.invalid
          ? themeGet("colors.dangerDark")(props)
          : props.valid
            ? themeGet("colors.successDark")(props)
            : themeGet("colors.primary")(props),
      },
      "&:focus": {
        outline: "0",
        boxShadow: props.invalid
          ? themeGet("shadows.thickOutline")(props) +
            " " +
            themeGet("colors.danger30")(props)
          : props.valid
            ? themeGet("shadows.thickOutline")(props) +
              " " +
              themeGet("colors.success30")(props)
            : themeGet("shadows.thickOutline")(props) +
              " " +
              themeGet("colors.primary30")(props),
        borderColor: props.invalid
          ? themeGet("colors.dangerDark")(props)
          : props.valid
            ? themeGet("colors.successDark")(props)
            : themeGet("colors.primary")(props),
      },
    }),
  InputStyles,
  (props) => (props.autoGrow ? css(autoGrowStyles(props)) : css({}))
);

const Label = styled("label").withConfig({ shouldForwardProp })(
  (props) =>
    css({
      display: "block",
      zIndex: "2",
      textAlign: "left",
      fontSize: themeGet("fontSizes.1")(props),
      fontWeight: props.bold
        ? themeGet("fontWeights.2")(props)
        : themeGet("fontWeights.1")(props),
      transition: themeGet("transition.transitionDefault")(props),
      mb: props.floating ? 0 : "xs",
      span: {
        color: themeGet("colors.danger")(props),
        fontWeight: themeGet("fontWeights.2")(props),
      },
      color: props.valid
        ? themeGet("colors.successDark")(props)
        : props.invalid
          ? themeGet("colors.dangerDark")(props)
          : themeGet("colors.bodyText")(props),
    }),
  LabelStyles
);

const Asterisk = () => {
  return <span> *</span>;
};

const TextArea = React.forwardRef((props, ref) => {
  const {
    id,
    value,
    children,
    disabled,
    label,
    invalid,
    valid,
    fullWidth,
    mandatory,
    bold,
    cols,
    rows,
    resize,
    onChange,
    defaultValue,
    autoFocus,
    autoGrow = false,
    autoGrowMaxHeight = DEFAULT_AUTO_GROW_MAX_HEIGHT,
    theme,
    WrapperStyles,
    LabelStyles,
    ariaLabel,
    onKeyDown,
    InputStyles,
  } = props;

  // Controlled when `value` is passed; otherwise uncontrolled via defaultValue.
  // Children used to be rendered as textarea content — keep as legacy seed only.
  const legacyChildrenText =
    typeof children === "string" || typeof children === "number"
      ? String(children)
      : undefined;
  const inputValueProps =
    value !== undefined
      ? { value }
      : {
          defaultValue:
            defaultValue !== undefined ? defaultValue : legacyChildrenText,
        };

  // Fallback height for browsers without `field-sizing`, and the `minHeight` basis.
  const effectiveRows = rows ?? (autoGrow ? "1" : undefined);

  const component = (
    <Wrapper fullWidth={fullWidth} {...WrapperStyles}>
      {label && (
        <Label
          invalid={invalid}
          valid={valid}
          htmlFor={id}
          mandatory={mandatory}
          bold={bold}
          {...LabelStyles}
        >
          {label} {mandatory && <Asterisk />}
        </Label>
      )}
      <Input
        ref={ref}
        id={id}
        fullWidth={fullWidth}
        cols={cols}
        disabled={disabled}
        rows={effectiveRows}
        resize={resize}
        autoGrow={autoGrow}
        autoGrowMaxHeight={autoGrowMaxHeight}
        autoFocus={autoFocus}
        valid={valid}
        invalid={invalid}
        onChange={onChange}
        data-testid={props["data-testid"]}
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
        {...inputValueProps}
        {...InputStyles}
      />
    </Wrapper>
  );

  return theme ? (
    <ThemeProvider theme={theme}>{component}</ThemeProvider>
  ) : (
    component
  );
});

TextArea.propTypes = {
  /** Must be used to specify a unique ID. */
  id: PropTypes.string.isRequired,
  /** Use value to specify text in text area. */
  value: PropTypes.string,
  /** @deprecated Prefer `value` or `defaultValue`. Kept so legacy children seed still work. */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Specifies the text for the TextArea label. */
  label: PropTypes.string,
  /** Specifies aria-label for TextArea. This is only required if not using the label prop.*/
  ariaLabel: (props, propName) => {
    if (!props.label && (props[propName] == null || props[propName] === "")) {
      return new Error(
        `Missing prop \`${propName}\` not specified for TextArea component. When \`label\` is not provided, \`${propName}\` is required.`
      );
    }
    if (props[propName] && typeof props[propName] !== "string") {
      return new Error(
        `Invalid propType \`${propName}\` supplied to TextArea component. Expected \`string\`, received \`${typeof props[
          propName
        ]}\`.`
      );
    }
    return null;
  },
  /** Set the label text to bold font-weight. */
  bold: PropTypes.bool,
  /** Sets disabled attribute and styling */
  disabled: PropTypes.bool,
  /** Makes text box take up full width of parent */
  fullWidth: PropTypes.bool,
  /** Sets specified width of input area in columns */
  cols: PropTypes.string,
  /** Sets specified height of input area in rows */
  rows: PropTypes.string,
  /** Applies invalid styles (coloured with "warning" colour from design system) */
  invalid: PropTypes.bool,
  /** Applies valid styles (coloured with "success" colour from design system) */
  valid: PropTypes.bool,
  /** Shows asterisk to denote a mandatory field */
  mandatory: PropTypes.bool,
  /** Set additional styles for the `Wrapper` if needed */
  WrapperStyles: PropTypes.object,
  /** Set additional styles for the `Label` if needed */
  LabelStyles: PropTypes.object,
  /** Set additional styles for the `Input` textarea if needed */
  InputStyles: PropTypes.object,
  /** Specifies `data-testid` for testing */
  "data-testid": PropTypes.string,
  /** Specifies whether TextArea should automatically get focus */
  autoFocus: PropTypes.bool,
  /** Grow height with content (AI chat composers, long notes). Forces resize="none".
   * Uses CSS `field-sizing`; browsers without it keep the fixed `rows` height. */
  autoGrow: PropTypes.bool,
  /** Max height in px when autoGrow is on; content scrolls beyond this. Default 200. */
  autoGrowMaxHeight: PropTypes.number,
  /** Specifies `onChange` function */
  onChange: PropTypes.func,
  /** Specifies `textarea` default value (different from placeholder) */
  defaultValue: PropTypes.string,
  /** Specifies the design theme object */
  theme: PropTypes.object,
  /** Controls how the textarea can be resized (none, both, horizontal, vertical) */
  resize: PropTypes.oneOf(["none", "both", "horizontal", "vertical"]),
  onKeyDown: PropTypes.func,
};

export default TextArea;
